<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Dashboard
    </h2>

    <div class="section-eyebrow mb-2">Month to Date</div>
    <div class="grid mb-4">
      <div
        v-for="card in monthToDateCards"
        :key="card.title"
        class="col-12 sm:col-6 lg:col-3"
      >
        <StatCard
          :title="card.title"
          :content-str="card.value"
          :loader="monthToDateLoading ? 1 : 0"
        />
      </div>
    </div>

    <div class="section-eyebrow mb-2">By Date Range</div>
    <div class="mb-4 flex flex-column sm:flex-row gap-2 sm:gap-3">
      <Calendar
        v-model="dateRange"
        :showIcon="true"
        :number-of-months="2"
        :selectionMode="'range'"
        @update:modelValue="setDateRange"
      >
      </Calendar>
      <Button @click="fetchOnDateRange">Fetch Data</Button>
    </div>

    <DashboardSection
      v-for="section in sections"
      :key="section.dataMode"
      v-bind="section"
      :data="data[section.dataMode]"
      :loading="loading[section.dataMode]"
      :rows="rows"
      :totalRecords="totalRecords[section.dataMode] * 1"
      @fetch-data="fetchData"
      @download-csv="downloadCsv(section.dataMode, section.dataMode + '.csv')"
      @open-schedule-dialog="openScheduleDialog"
    />
  </div>
  <AddTableScheduledReportDialog
    :data-mode="scheduleDataMode"
    :visible="scheduleDialogVisible"
    user-role="group"
    :user-token="scheduleToken"
    @hide="closeScheduleDialog()"
  />
</template>

<script>
import { ref, reactive, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { API_BASE_URL } from "../consts";
import Calendar from "primevue/calendar";
import Button from "primevue/button";
import { useStore } from "vuex";
import { adjustDate, formatCurrencyNumber } from "../helpers";
import DashboardSection from "./DashboardSection.vue";
import AddTableScheduledReportDialog from "./AddTableScheduledReportDialog.vue";
import StatCard from "./AdminNewDashboard/AdminNewDashboardComponents/StatCard.vue";

const sumMonthlyTotals = (rows) => {
  const totals = rows.reduce(
    (acc, row) => {
      acc.loads += Number(row.loads) || 0;
      acc.impressions += Number(row.impressions) || 0;
      acc.revenue += Number(row.revenue) || 0;
      return acc;
    },
    { loads: 0, impressions: 0, revenue: 0 }
  );

  return {
    ...totals,
    ecpm:
      totals.impressions > 0 ? (totals.revenue * 1000) / totals.impressions : 0,
  };
};

export default {
  components: {
    Calendar,
    Button,
    DashboardSection,
    AddTableScheduledReportDialog,
    StatCard,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const groupAdminData = JSON.parse(localStorage.getItem("groupAdmin"));
    const sellerGroupId = router.currentRoute.value.params.sellerGroupId;
    const isAdminLoggedIn = store.getters.isAdminLoggedIn;

    const scheduleDialogVisible = ref(false);
    const scheduleDataMode = ref("");
    const scheduleToken = ref("");

    const openScheduleDialog = ({ dataMode }) => {
      scheduleDataMode.value = dataMode;
      scheduleDialogVisible.value = true;
    };

    const closeScheduleDialog = () => {
      scheduleDialogVisible.value = false;
    };

    const createColumns = (
      includeUrl = false,
      includeGeo = false,
      includeSellerId = false
    ) => {
      const columns = [
        { header: "Date", field: "date_trunc" },
        { header: "Player Loads", field: "loads" },
        { header: "Ad Impressions", field: "impressions" },
        { header: "Revenue", field: "revenue" },
      ];

      if (includeUrl) columns.splice(1, 0, { header: "Domain", field: "url" });
      if (includeGeo) columns.splice(1, 0, { header: "Geo", field: "geo" });
      if (includeSellerId)
        columns.splice(1, 0, { header: "Seller ID", field: "seller_id" });

      if (groupAdminData?.show_gross_profit) {
        columns.push({ header: "Gross Profit", field: "gross_profit" });
      }
      if (groupAdminData?.show_gross_revenue) {
        columns.push({ header: "Gross Revenue", field: "gross_revenue" });
      }

      if (router.currentRoute.value.name === "PartnerGroupDashboard") {
        columns.push(
          { header: "Cost", field: "cost" },
          { header: "Profit", field: "profit" }
        );
      }

      columns.push({ header: "ECPM", field: "ecpm" });
      if (groupAdminData?.show_cpm)
        columns.push({ header: "CPM", field: "cpm" });

      return columns;
    };

    const sections = [
      {
        title: "Daily Overview",
        dataMode: "partner_daily",
        columns: createColumns(),
        showChart: true,
        chartLegend: "bottom",
      },
      {
        title: "Per Domain",
        dataMode: "partner_per_domain_daily",
        columns: createColumns(true),
        showChart: true,
        chartLegend: "bottom",
      },
      {
        title: "Per Geo Location",
        dataMode: "partner_per_geo_daily",
        columns: createColumns(false, true),
        showChart: true,
        chartLegend: "bottom",
      },
      {
        title: "Per Seller",
        dataMode: "partner_per_domain_and_seller_daily",
        columns: createColumns(true, false, true),
        showChart: false,
        chartLegend: "bottom",
      },
      {
        title: "Monthly Overview",
        dataMode: "partner_monthly",
        columns: createColumns(),
        showChart: false,
      },
      {
        title: "Per Seller Monthly Overview",
        dataMode: "partner_per_seller_monthly",
        columns: createColumns(true, false, true),
        showChart: false,
      },
    ];

    const rows = ref(10);
    const totalRecords = reactive({
      partner_daily: 0,
      partner_per_domain_daily: 0,
      partner_per_geo_daily: 0,
      partner_per_seller_daily: 0,
      partner_monthly: 0,
    });
    const data = reactive({
      partner_daily: [],
      partner_per_domain_daily: [],
      partner_per_geo_daily: [],
      partner_per_seller_daily: [],
      partner_monthly: [],
    });

    const csvData = reactive({
      partner_daily: [],
      partner_per_domain_daily: [],
      partner_per_geo_daily: [],
      partner_per_seller_daily: [],
      partner_monthly: [],
    });

    const loading = reactive({
      partner_daily: false,
      partner_per_domain_daily: false,
      partner_per_geo_daily: false,
      partner_per_seller_daily: false,
      partner_monthly: false,
    });

    const dateRange = ref([
      new Date(new Date().setDate(new Date().getDate() - 7)),
      new Date(),
    ]);

    const setDateRange = (value) => {
      dateRange.value = value;
    };

    const monthToDateTotals = reactive({
      loads: 0,
      impressions: 0,
      revenue: 0,
      ecpm: 0,
    });
    const monthToDateLoading = ref(true);

    const monthToDateCards = computed(() => [
      {
        title: "Player Loads",
        value: monthToDateTotals.loads.toLocaleString(),
      },
      {
        title: "Ad Impressions",
        value: monthToDateTotals.impressions.toLocaleString(),
      },
      {
        title: "Revenue",
        value: formatCurrencyNumber(monthToDateTotals.revenue),
      },
      { title: "ECPM", value: formatCurrencyNumber(monthToDateTotals.ecpm) },
    ]);

    const fetchMonthToDateTotals = async () => {
      try {
        monthToDateLoading.value = true;
        const token = isAdminLoggedIn
          ? localStorage.getItem("adminToken")
          : sellerGroupId
          ? localStorage.getItem("token")
          : localStorage.getItem("groupAdminToken");

        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const startDate = adjustDate(monthStart);
        const endDate = adjustDate(now);

        const params = {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
          dataMode: "partner_monthly_all",
          show_cpm: groupAdminData?.show_cpm,
        };

        if (sellerGroupId) {
          params.sellerGroupId = sellerGroupId;
        }

        const { data: responseData } = await axios.get(
          `${API_BASE_URL}/group-admin/dashboard`,
          {
            params,
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const monthRows = responseData?.success
          ? responseData.data.partner_monthly || []
          : [];

        Object.assign(monthToDateTotals, sumMonthlyTotals(monthRows));
      } catch (error) {
        console.error("Error fetching month-to-date totals:", error);
        Object.assign(monthToDateTotals, sumMonthlyTotals([]));
      } finally {
        monthToDateLoading.value = false;
      }
    };

    const fetchData = async ({
      dataMode,
      page = 0,
      rows = 10,
      sortField,
      sortOrder,
      fetchCsvData = false,
    }) => {
      try {
        loading[dataMode] = true;
        const token = isAdminLoggedIn
          ? localStorage.getItem("adminToken")
          : sellerGroupId
          ? localStorage.getItem("token")
          : localStorage.getItem("groupAdminToken");

        scheduleToken.value = token;

        const startDate = adjustDate(dateRange.value?.[0]);
        const endDate = adjustDate(dateRange.value?.[1]);

        const params = {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
          dataMode: fetchCsvData ? `${dataMode}_all` : dataMode,
          page,
          rows,
          sortField,
          sortOrder,
          show_cpm: groupAdminData?.show_cpm,
        };

        if (sellerGroupId) {
          params.sellerGroupId = sellerGroupId;
        }

        const { data: responseData } = await axios.get(
          `${API_BASE_URL}/group-admin/dashboard`,
          {
            params,
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (responseData.success) {
          if (fetchCsvData) {
            csvData[dataMode] = responseData.data[`${dataMode}`] || [];
          } else {
            data[dataMode] = responseData.data[dataMode] || [];
            totalRecords[dataMode] =
              responseData.data[dataMode]?.[0]?.count || 0;
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (fetchCsvData) {
          csvData[dataMode] = [];
        } else {
          data[dataMode] = [];
          totalRecords[dataMode] = 0;
        }
      } finally {
        loading[dataMode] = false;
      }
    };

    const fetchCsvData = async (dataMode) => {
      await fetchData({ dataMode, fetchCsvData: true });
    };

    const fetchOnDateRange = () => {
      sections.forEach((section) => {
        fetchData({ dataMode: section.dataMode });
      });
    };

    onMounted(() => {
      sections.forEach((section) => {
        fetchData({ dataMode: section.dataMode });
      });
      fetchMonthToDateTotals();
    });

    const downloadCsv = async (dataMode, filename) => {
      await fetchCsvData(dataMode);
      const fetchedData = csvData[dataMode];
      const headers = Object.keys(fetchedData[0]);

      // add headers
      const csvContent =
        "data:text/csv;charset=utf-8," +
        headers.join(",") +
        "\n" +
        fetchedData.map((row) => Object.values(row).join(",")).join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return {
      sections,
      rows,
      totalRecords,
      data,
      loading,
      dateRange,
      setDateRange,
      fetchData,
      fetchOnDateRange,
      fetchCsvData,
      downloadCsv,
      scheduleDialogVisible,
      scheduleDataMode,
      scheduleToken,
      openScheduleDialog,
      closeScheduleDialog,
      monthToDateCards,
      monthToDateLoading,
    };
  },
};
</script>

<style scoped>
.section-eyebrow {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-color-secondary);
}
</style>
